import * as React from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Heading2, Quote, Undo, Redo, Link as LinkIcon, Strikethrough, Eraser,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface RichTextEditorProps {
  /** HTML string. Plain text is also accepted; tiptap wraps it in a paragraph. */
  value?: string | null;
  /** Fires with HTML on every change. Returns `''` when fully cleared. */
  onChange: (html: string) => void;
  placeholder?: string;
  className?: string;
  /** Min height for the editable area. Defaults to ~6 lines. */
  minHeight?: string;
  disabled?: boolean;
}

function ToolbarButton({
  editor, onClick, active, disabled, title, children,
}: {
  editor: Editor | null;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={(e) => { e.preventDefault(); onClick(); }}
      disabled={disabled || !editor}
      className={cn(
        'h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-accent hover:text-accent-foreground disabled:opacity-40 disabled:pointer-events-none',
        active && 'bg-accent text-accent-foreground',
      )}
    >
      {children}
    </button>
  );
}

function Divider() {
  return <span className="mx-0.5 h-5 w-px bg-border" aria-hidden />;
}

/**
 * Minimal Tiptap-backed rich-text editor with shadcn-style chrome.
 *
 * Stores HTML so server-side renderers (e.g. the IoT email send service) can
 * pass the value through to the email body unchanged. The toolbar covers the
 * common editing affordances (bold, italic, underline, strike, h2, quote,
 * lists, link, undo/redo, clear formatting); structural HTML is hidden behind
 * formatted text — users see "**bold**" rendered, not `<strong>`.
 */
export function RichTextEditor({
  value, onChange, placeholder, className, minHeight = '160px', disabled,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] } }),
      Underline,
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: 'text-primary underline' } }),
    ],
    content: value ?? '',
    editable: !disabled,
    editorProps: {
      attributes: {
        class: cn(
          'prose-style focus:outline-none px-3 py-2 leading-relaxed',
          '[&_p]:my-1 [&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:text-lg [&_h2]:font-semibold',
          '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5',
          '[&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:italic [&_blockquote]:text-muted-foreground',
          '[&_a]:text-primary [&_a]:underline',
          '[&_strong]:font-semibold [&_em]:italic',
        ),
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // tiptap emits "<p></p>" for empty — collapse to '' so callers can
      // detect "no content".
      const isEmpty = editor.isEmpty;
      onChange(isEmpty ? '' : html);
    },
  });

  // External value changes (e.g. loading a different record into the same
  // editor instance) — re-set the editor content. Skip if it's already the
  // same to avoid clobbering the user's caret.
  React.useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const next = value ?? '';
    // tiptap normalises empty as '<p></p>'; treat both as equal.
    const sameEmpty = (current === '<p></p>' || current === '') && (next === '' || next === '<p></p>');
    if (current !== next && !sameEmpty) {
      editor.commands.setContent(next, { emitUpdate: false });
    }
  }, [value, editor]);

  React.useEffect(() => () => { editor?.destroy(); }, [editor]);

  function promptLink() {
    if (!editor) return;
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', prev ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }

  return (
    <div className={cn('rounded-md border bg-background', disabled && 'opacity-60', className)}>
      <div className="flex items-center gap-0.5 border-b px-1 py-1 flex-wrap">
        <ToolbarButton editor={editor} title="Bold (Ctrl+B)" onClick={() => editor?.chain().focus().toggleBold().run()} active={editor?.isActive('bold')}>
          <Bold className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton editor={editor} title="Italic (Ctrl+I)" onClick={() => editor?.chain().focus().toggleItalic().run()} active={editor?.isActive('italic')}>
          <Italic className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton editor={editor} title="Underline (Ctrl+U)" onClick={() => editor?.chain().focus().toggleUnderline().run()} active={editor?.isActive('underline')}>
          <UnderlineIcon className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton editor={editor} title="Strikethrough" onClick={() => editor?.chain().focus().toggleStrike().run()} active={editor?.isActive('strike')}>
          <Strikethrough className="h-3.5 w-3.5" />
        </ToolbarButton>
        <Divider />
        <ToolbarButton editor={editor} title="Heading" onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} active={editor?.isActive('heading', { level: 2 })}>
          <Heading2 className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton editor={editor} title="Quote" onClick={() => editor?.chain().focus().toggleBlockquote().run()} active={editor?.isActive('blockquote')}>
          <Quote className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton editor={editor} title="Bulleted list" onClick={() => editor?.chain().focus().toggleBulletList().run()} active={editor?.isActive('bulletList')}>
          <List className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton editor={editor} title="Numbered list" onClick={() => editor?.chain().focus().toggleOrderedList().run()} active={editor?.isActive('orderedList')}>
          <ListOrdered className="h-3.5 w-3.5" />
        </ToolbarButton>
        <Divider />
        <ToolbarButton editor={editor} title="Link" onClick={promptLink} active={editor?.isActive('link')}>
          <LinkIcon className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton editor={editor} title="Clear formatting" onClick={() => editor?.chain().focus().clearNodes().unsetAllMarks().run()}>
          <Eraser className="h-3.5 w-3.5" />
        </ToolbarButton>
        <Divider />
        <ToolbarButton editor={editor} title="Undo (Ctrl+Z)" onClick={() => editor?.chain().focus().undo().run()} disabled={!editor?.can().undo()}>
          <Undo className="h-3.5 w-3.5" />
        </ToolbarButton>
        <ToolbarButton editor={editor} title="Redo (Ctrl+Y)" onClick={() => editor?.chain().focus().redo().run()} disabled={!editor?.can().redo()}>
          <Redo className="h-3.5 w-3.5" />
        </ToolbarButton>
      </div>
      <div
        className="text-sm relative"
        style={{ minHeight }}
        // Show the placeholder when the editor is empty. tiptap's own
        // Placeholder extension would also work; for simplicity we render an
        // overlay span that the editor sits on top of.
      >
        {placeholder && editor?.isEmpty && (
          <div className="pointer-events-none absolute inset-0 px-3 py-2 text-muted-foreground select-none">
            {placeholder}
          </div>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
